import React, { useEffect } from "react";
import NavbarPage from "../../components/Navbar/Navbar_Page";
import Footer from "../../components/Footer/footer";
import "../Index10/Index10.css";
import "./Legal.css";

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="home-ten legal-page">
      <NavbarPage navItems={[]} navClass="" />
      <div className="legal-content">
        <h1>Terms of Service</h1>
        <p className="legal-updated">Last updated: March 28, 2026</p>

        <p>
          These Terms of Service ("Terms") govern your access to and use of Public Backlog
          ("Service"), operated by CmSoftware ("Company", "we", "us", or "our"). By creating
          an account or using the Service, you agree to be bound by these Terms. If you do not
          agree, do not use the Service.
        </p>

        <hr />

        <h2>1. Definitions</h2>
        <ul>
          <li><strong>"Service"</strong> refers to the Public Backlog platform accessible at app.publicbacklog.com, including all APIs, SDKs, integrations, and related services.</li>
          <li><strong>"User"</strong> means any individual who creates an account on the Service.</li>
          <li><strong>"Organization"</strong> means a team or business entity created within the Service by a User.</li>
          <li><strong>"Organization User"</strong> means any individual granted access to an Organization, whether as an administrator or standard user.</li>
          <li><strong>"Backlog"</strong> means a feedback board created within an Organization.</li>
          <li><strong>"Content"</strong> means any data, text, files, images, or other materials submitted to the Service, including tickets, comments, votes, notes, and file attachments.</li>
        </ul>

        <h2>2. Account Registration</h2>
        <p>
          To use the Service, you must create an account by providing a valid email address, your name, and a password.
          You may also register using Google OAuth or Single Sign-On (SSO) through your organization's identity provider.
        </p>
        <ul>
          <li>You must be at least 18 years of age to use the Service.</li>
          <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
          <li>You are responsible for all activity that occurs under your account.</li>
          <li>You must provide accurate and complete information during registration and keep it up to date.</li>
          <li>You must notify us immediately of any unauthorized use of your account.</li>
        </ul>

        <h2>3. Organizations and Access</h2>
        <p>
          Users may create Organizations and invite others to join as Organization Users.
          Organization administrators are responsible for managing access, roles, and permissions within their Organization.
        </p>
        <ul>
          <li>Organization owners are responsible for the conduct of all users within their Organization.</li>
          <li>Organizations may be configured as public (visible to anyone) or private (restricted to members).</li>
          <li>Backlogs within an Organization may have their own public or private visibility settings.</li>
          <li>The Organization owner may enable or disable anonymous feedback submission and email requirements.</li>
        </ul>

        <h2>4. Acceptable Use</h2>
        <p>You agree not to use the Service to:</p>
        <ul>
          <li>Violate any applicable laws, regulations, or third-party rights.</li>
          <li>Submit Content that is unlawful, defamatory, obscene, threatening, or otherwise objectionable.</li>
          <li>Impersonate any person or entity or misrepresent your affiliation.</li>
          <li>Upload malicious code, viruses, or files designed to disrupt the Service.</li>
          <li>Attempt to gain unauthorized access to any part of the Service or its infrastructure.</li>
          <li>Use the Service to send unsolicited communications or spam.</li>
          <li>Scrape, crawl, or harvest data from the Service without permission.</li>
          <li>Interfere with or disrupt the Service or servers or networks connected to the Service.</li>
          <li>Circumvent any rate limiting, access controls, or security measures.</li>
          <li>Use the Service for any purpose that competes directly with the Service.</li>
        </ul>
        <p>
          We reserve the right to suspend or terminate your account for violations of these terms,
          with or without prior notice.
        </p>

        <h2>5. Content and Intellectual Property</h2>
        <h3>5.1 Your Content</h3>
        <p>
          You retain ownership of all Content you submit to the Service. By submitting Content,
          you grant us a worldwide, non-exclusive, royalty-free license to use, store, display,
          reproduce, and distribute your Content solely for the purpose of operating and improving the Service.
        </p>
        <p>
          When you submit Content to a public Backlog or public roadmap, you acknowledge that
          this Content will be visible to other users and potentially the general public.
        </p>

        <h3>5.2 Our Intellectual Property</h3>
        <p>
          The Service, including its design, features, code, documentation, logos, and trademarks,
          is owned by CmSoftware and is protected by intellectual property laws. These Terms do
          not grant you any rights to use our trademarks, logos, or branding without prior written consent.
        </p>

        <h3>5.3 Feedback About the Service</h3>
        <p>
          Any suggestions, ideas, or feedback you provide about the Service itself (as distinct
          from Content submitted to your backlogs) may be used by us without obligation to you.
        </p>

        <h2>6. Third-Party Integrations</h2>
        <p>
          The Service offers integrations with third-party platforms including but not limited to
          GitHub, Jira, Slack, Linear, Asana, Intercom, Zendesk, Salesforce, HubSpot, Microsoft Teams,
          Azure DevOps, ClickUp, Notion, Help Scout, Freshdesk, Zapier, and Segment.
        </p>
        <ul>
          <li>
            Integrations are enabled at your discretion. When you connect an integration, you authorize
            the Service to exchange data with that third-party platform on your behalf.
          </li>
          <li>
            Third-party services are governed by their own terms of service and privacy policies.
            We are not responsible for the practices of third-party services.
          </li>
          <li>
            You are responsible for ensuring that your use of integrations complies with the terms of
            both this Service and the third-party platform.
          </li>
          <li>
            Integration credentials (OAuth tokens, API keys) are stored encrypted and used solely to
            maintain the integration connection.
          </li>
        </ul>

        <h2>7. API and SDK Usage</h2>
        <p>
          The Service provides APIs and SDKs for programmatic access. Use of the API is subject to
          these Terms and any additional API-specific documentation. We reserve the right to impose
          rate limits and usage restrictions on API access.
        </p>

        <h2>8. Payment and Subscriptions</h2>
        <p>
          Certain features of the Service require a paid subscription. Payment is processed through
          Stripe, a third-party payment processor. By subscribing to a paid plan, you agree to the following:
        </p>
        <ul>
          <li>You authorize us to charge the payment method on file for recurring subscription fees.</li>
          <li>Subscription fees are billed in advance on a recurring basis (monthly or annually, as selected).</li>
          <li>All fees are non-refundable except as required by law or as explicitly stated in these Terms.</li>
          <li>We may change pricing with at least 30 days' notice. Continued use after a price change constitutes acceptance.</li>
          <li>If payment fails, we may suspend access to paid features until the payment issue is resolved.</li>
        </ul>
        <p>
          We do not store your credit card details. All payment information is handled directly by Stripe
          in accordance with their security standards (PCI DSS Level 1).
        </p>

        <h2>9. Free Plan</h2>
        <p>
          The Service offers a free tier with limited features. Free accounts are subject to the same
          Terms as paid accounts. We reserve the right to modify or discontinue the free tier at any time,
          with reasonable notice.
        </p>

        <h2>10. Data and Privacy</h2>
        <p>
          Your use of the Service is also governed by our{" "}
          <a href="/privacy-policy">Privacy Policy</a>, which describes how we collect, use, and
          protect your personal information. By using the Service, you consent to the practices
          described in our Privacy Policy.
        </p>

        <h2>11. Service Availability and Modifications</h2>
        <ul>
          <li>We strive to maintain high availability but do not guarantee uninterrupted access to the Service.</li>
          <li>We may modify, update, or discontinue features of the Service at any time.</li>
          <li>Scheduled maintenance will be communicated in advance when possible.</li>
          <li>We are not liable for any loss or damage resulting from Service downtime or modifications.</li>
        </ul>

        <h2>12. Termination</h2>
        <h3>12.1 By You</h3>
        <p>
          You may close your account at any time by contacting us. Upon closure, your personal data
          will be handled in accordance with our Privacy Policy.
        </p>

        <h3>12.2 By Us</h3>
        <p>
          We may suspend or terminate your account if you violate these Terms, engage in abusive behavior,
          or for any other reason at our sole discretion, with or without notice. Upon termination:
        </p>
        <ul>
          <li>Your right to access the Service ceases immediately.</li>
          <li>We may delete your Content after a reasonable retention period (typically 30 days).</li>
          <li>Provisions that by their nature should survive termination will survive (including sections on intellectual property, limitation of liability, and indemnification).</li>
        </ul>

        <h2>13. Disclaimer of Warranties</h2>
        <p>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
          EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY,
          FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE
          WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE, OR THAT ANY DEFECTS WILL BE CORRECTED.
        </p>

        <h2>14. Limitation of Liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, CMSOFTWARE SHALL NOT BE LIABLE FOR ANY INDIRECT,
          INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE,
          DATA, OR USE, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE, REGARDLESS OF THE THEORY
          OF LIABILITY.
        </p>
        <p>
          OUR TOTAL AGGREGATE LIABILITY FOR ALL CLAIMS RELATED TO THE SERVICE SHALL NOT EXCEED THE
          AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR ONE HUNDRED U.S. DOLLARS
          ($100), WHICHEVER IS GREATER.
        </p>

        <h2>15. Indemnification</h2>
        <p>
          You agree to indemnify, defend, and hold harmless CmSoftware and its officers, directors,
          employees, and agents from and against any claims, damages, losses, liabilities, and expenses
          (including reasonable attorneys' fees) arising out of or related to your use of the Service,
          your violation of these Terms, or your violation of any rights of another party.
        </p>

        <h2>16. Governing Law and Disputes</h2>
        <p>
          These Terms are governed by and construed in accordance with the laws of the United States.
          Any disputes arising under these Terms shall be resolved through binding arbitration in
          accordance with the rules of the American Arbitration Association, except that either party
          may seek injunctive relief in any court of competent jurisdiction.
        </p>

        <h2>17. Changes to These Terms</h2>
        <p>
          We may update these Terms from time to time. When we make material changes, we will notify
          you by email or through a notice on the Service. Your continued use of the Service after
          changes become effective constitutes your acceptance of the revised Terms.
        </p>

        <h2>18. Miscellaneous</h2>
        <ul>
          <li><strong>Entire Agreement:</strong> These Terms, together with the Privacy Policy, constitute the entire agreement between you and CmSoftware regarding the Service.</li>
          <li><strong>Severability:</strong> If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.</li>
          <li><strong>Waiver:</strong> Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision.</li>
          <li><strong>Assignment:</strong> You may not assign your rights under these Terms without our written consent. We may assign our rights without restriction.</li>
        </ul>

        <h2>19. Contact</h2>
        <p>
          If you have any questions about these Terms, please contact us at:
        </p>
        <ul>
          <li>Email: <a href="mailto:support@publicbacklog.com">support@publicbacklog.com</a></li>
          <li>Website: <a href="https://publicbacklog.com">publicbacklog.com</a></li>
        </ul>
      </div>
      <Footer />
    </div>
  );
};

export default TermsOfService;
